import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Pencil, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useClearFlagGate, useFlagGates, useUpsertFlagGate } from '@/hooks/use-feature-flags';
import { FlagWriteConfirmDialog } from '@/components/feature-flags/flag-write-confirm-dialog';
import { requiresConfirmation } from '@/lib/api-env';
import { env } from '@/config/env';
import { FEATURE_FLAG_GATE_PLATFORMS, SEMVER, getEnforcedFlagDisableCopy } from '@/types/feature-flag';
import type {
  FeatureFlagDefinition,
  FeatureFlagGate,
  FeatureFlagGatePlatform,
  FlagScope,
  UpsertFlagGateBody,
} from '@/types/feature-flag';

// Web clients don't send x-app-version, so a version condition can never
// match them — 'all' genuinely means "clients that report a version".
const PLATFORM_LABELS: Record<FeatureFlagGatePlatform, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  all: 'All app platforms (iOS + Android)',
};

/**
 * The form deliberately expresses only the two things operators mean —
 * "turn this off on a platform" and "this feature needs a minimum app
 * version" — and generates the withholding row (`< version`, off) itself.
 * Exposing the raw operator/value pair caused the same inversion three
 * times in one day: "needs 48.3.11+" reads naturally as `>= 48.3.11`,
 * which as a gate row means the exact opposite.
 */
type GateIntent = 'platform-off' | 'min-version';

/** Rows the form can express render as their intent; anything else
 * (API-authored) renders via the raw fallback. Derives from `gateIntent` so
 * the badge and the form can never disagree about what is representable —
 * e.g. a version-conditioned web row is inert (web reports no version) and
 * must render raw, not as "Off below X". */
function describeIntent(platform: FeatureFlagGatePlatform, gate: FeatureFlagGate): string | null {
  switch (gateIntent(platform, gate)) {
    case 'min-version':
      return `Off below ${gate.version}`;
    case 'platform-off':
      return 'Off';
    default:
      return null;
  }
}

interface FlagGatesPanelProps {
  scope: FlagScope;
  definition: FeatureFlagDefinition | null;
  onClose: () => void;
}

export function FlagGatesPanel({ scope, definition, onClose }: FlagGatesPanelProps) {
  const key = definition?.key ?? '';
  const enforced = definition?.enforced ?? false;
  const { data, isLoading } = useFlagGates(scope, key, definition !== null && !enforced);
  const upsertMutation = useUpsertFlagGate();
  const clearMutation = useClearFlagGate();
  const [editingPlatform, setEditingPlatform] = useState<FeatureFlagGatePlatform | null>(null);
  // On the prod dashboard every flag toggle pauses for confirmation; narrowing
  // the same flag to a platform reaches the same live users, so it does too.
  const prodGuard = requiresConfirmation(env.VITE_API_URL);
  const [pendingWrite, setPendingWrite] = useState<{
    platform: FeatureFlagGatePlatform;
    body: UpsertFlagGateBody;
  } | null>(null);
  const [pendingClear, setPendingClear] = useState<FeatureFlagGatePlatform | null>(null);

  const busy = upsertMutation.isPending || clearMutation.isPending;
  const gatesByPlatform = new Map(
    (data?.gates ?? []).map(gate => [gate.platform, gate] as const)
  );

  /** Same reason as the rules panel: the component outlives any one flag, so
   * every close path clears the editor and any pending confirmation. */
  const closePanel = () => {
    setEditingPlatform(null);
    setPendingWrite(null);
    setPendingClear(null);
    onClose();
  };

  const isPlatformBusy = (platform: FeatureFlagGatePlatform) =>
    (upsertMutation.isPending && upsertMutation.variables?.platform === platform) ||
    (clearMutation.isPending && clearMutation.variables?.platform === platform);

  const applyUpsert = (platform: FeatureFlagGatePlatform, body: UpsertFlagGateBody) =>
    upsertMutation.mutate(
      { scope, key, platform, body },
      { onSuccess: () => setEditingPlatform(null) }
    );

  const runUpsert = (platform: FeatureFlagGatePlatform, body: UpsertFlagGateBody) => {
    if (prodGuard) {
      setPendingWrite({ platform, body });
      return;
    }
    applyUpsert(platform, body);
  };

  return (
    <Dialog
      open={definition !== null}
      onOpenChange={next => {
        // Cancel is disabled while a write is in flight; Escape and the
        // overlay must not be a way around that.
        if (next || busy) return;
        closePanel();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Platform &amp; version overrides for {definition?.label}</DialogTitle>
          {!enforced && (
            <DialogDescription>
              An override only turns the flag off for the clients it names — everyone else gets
              the flag&apos;s stored value. Clearing an override removes that restriction.
            </DialogDescription>
          )}
        </DialogHeader>

        {enforced ? (
          // Enforcement reads the stored value directly and never evaluates a
          // gate — the API rejects a write here, so the panel stays read-only
          // rather than offer a control that would just 400.
          <Alert>
            <AlertDescription>
              <strong>{definition?.label}</strong> is enforced by the backend — it{' '}
              {getEnforcedFlagDisableCopy(definition?.key ?? '')} Platform and version overrides
              only apply to advisory flags.
            </AlertDescription>
          </Alert>
        ) : isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : (
          <div className="space-y-2">
            {FEATURE_FLAG_GATE_PLATFORMS.map(platform => {
              const gate = gatesByPlatform.get(platform) ?? null;
              const isEditing = editingPlatform === platform;
              const intentLabel = gate ? describeIntent(platform, gate) : null;

              return (
                <div key={platform} className="rounded-md border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-medium">{PLATFORM_LABELS[platform]}</span>
                      {gate ? (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {intentLabel ? (
                            <Badge variant="secondary">{intentLabel}</Badge>
                          ) : (
                            // API-authored row the form doesn't express;
                            // render it raw rather than mislabel it.
                            <Badge variant={gate.enabled ? 'default' : 'secondary'}>
                              {gate.enabled ? 'ON' : 'OFF'}
                              {gate.versionOp && gate.version
                                ? ` when app ${gate.versionOp} ${gate.version}`
                                : ''}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <p className="mt-1 text-xs text-muted-foreground">No override</p>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingPlatform(platform)}
                          disabled={busy}
                        >
                          <Pencil className="mr-1 h-3 w-3" />
                          {gate ? 'Edit' : 'Add'}
                        </Button>
                        {gate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive"
                            aria-label={`Clear the ${PLATFORM_LABELS[platform]} override`}
                            onClick={() => {
                              if (prodGuard) {
                                setPendingClear(platform);
                                return;
                              }
                              clearMutation.mutate({ scope, key, platform });
                            }}
                            disabled={busy}
                          >
                            {isPlatformBusy(platform) ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {isEditing && (
                    <GateRowForm
                      platform={platform}
                      initial={gate}
                      isSubmitting={isPlatformBusy(platform)}
                      onCancel={() => setEditingPlatform(null)}
                      onSubmit={body => runUpsert(platform, body)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={closePanel}
            disabled={busy}
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      <FlagWriteConfirmDialog
        open={pendingWrite !== null}
        title="Save this override?"
        description={`${definition?.label ?? 'This flag'} will be withheld from the clients this override names.`}
        prodGuard={prodGuard}
        busy={busy}
        onConfirm={() => {
          if (pendingWrite !== null) applyUpsert(pendingWrite.platform, pendingWrite.body);
          setPendingWrite(null);
        }}
        onCancel={() => setPendingWrite(null)}
      />

      <FlagWriteConfirmDialog
        open={pendingClear !== null}
        title="Clear this override?"
        description={`Those clients go back to ${definition?.label ?? 'the flag'}'s stored value, which may turn the feature back on for them.`}
        prodGuard={prodGuard}
        busy={busy}
        onConfirm={() => {
          if (pendingClear !== null) clearMutation.mutate({ scope, key, platform: pendingClear });
          setPendingClear(null);
        }}
        onCancel={() => setPendingClear(null)}
      />
    </Dialog>
  );
}

const gateFormSchema = z
  .object({
    intent: z.enum(['platform-off', 'min-version'], {
      message: 'Choose what this override should do',
    }),
    minVersion: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.intent === 'min-version') {
      const trimmed = value.minVersion.trim();
      if (trimmed.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minVersion'],
          message: 'Enter the minimum app version this feature needs',
        });
      } else if (!SEMVER.test(trimmed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['minVersion'],
          message: 'Must be valid semver (e.g., 3.5.0)',
        });
      }
    }
  });

type GateFormValues = z.infer<typeof gateFormSchema>;

/** The intent a row expresses on THIS platform's form, or null if the form
 * has no option that reproduces it (API-authored shapes: enabling overrides,
 * other operators, a version condition on web). */
function gateIntent(
  platform: FeatureFlagGatePlatform,
  gate: FeatureFlagGate
): GateIntent | null {
  if (gate.enabled) return null;
  if (gate.versionOp === '<' && gate.version) {
    return platform === 'web' ? null : 'min-version';
  }
  if (!gate.versionOp) {
    return platform === 'all' ? null : 'platform-off';
  }
  return null;
}

/** No gate yet -> sensible default. An unrepresentable gate -> no intent
 * preselected, so an unmodified Save cannot silently rewrite its meaning. */
function toGateFormValues(
  platform: FeatureFlagGatePlatform,
  gate: FeatureFlagGate | null
): Partial<GateFormValues> {
  if (!gate) {
    return {
      intent: platform === 'all' ? 'min-version' : 'platform-off',
      minVersion: '',
    };
  }
  const intent = gateIntent(platform, gate);
  // An unrepresentable gate's version must not carry into the replacement —
  // prefilled, one quick Save turns "> 5.0.0" into "< 5.0.0".
  return { intent: intent ?? undefined, minVersion: intent ? (gate.version ?? '') : '' };
}

interface GateRowFormProps {
  platform: FeatureFlagGatePlatform;
  initial: FeatureFlagGate | null;
  isSubmitting: boolean;
  onSubmit: (body: UpsertFlagGateBody) => void;
  onCancel: () => void;
}

function GateRowForm({ platform, initial, isSubmitting, onSubmit, onCancel }: GateRowFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<GateFormValues>({
    resolver: zodResolver(gateFormSchema),
    defaultValues: toGateFormValues(platform, initial),
  });

  const intent = useWatch({ control, name: 'intent' });

  // A row this form has no option for (enabling override, another operator,
  // a version condition on web) must not be silently rewritten by Save — no
  // intent is preselected, and choosing one is an explicit replacement.
  const unrepresentable = initial !== null && gateIntent(platform, initial) === null;

  // Web clients never report an app version, so a version condition can't
  // match them — the only meaningful web override is off/on-platform. The
  // 'all' row is the mirror image: the API requires a version condition
  // there, since an unconditional one would just duplicate the stored value.
  const intents: Array<{ value: GateIntent; label: string }> =
    platform === 'web'
      ? [{ value: 'platform-off', label: 'Turn off on Web' }]
      : platform === 'all'
        ? [{ value: 'min-version', label: 'Require a minimum app version' }]
        : [
            { value: 'platform-off', label: `Turn off on ${PLATFORM_LABELS[platform]}` },
            { value: 'min-version', label: 'Require a minimum app version' },
          ];

  return (
    <form
      onSubmit={handleSubmit(values =>
        onSubmit(
          values.intent === 'min-version'
            ? { versionOp: '<', version: values.minVersion.trim(), enabled: false }
            : { versionOp: null, version: null, enabled: false }
        )
      )}
      className="mt-3 space-y-3 border-t pt-3"
    >
      {unrepresentable && initial && (
        <Alert>
          <AlertDescription>
            The current rule (
            <code>
              {initial.enabled ? 'ON' : 'OFF'}
              {initial.versionOp && initial.version
                ? ` when app ${initial.versionOp} ${initial.version}`
                : ''}
            </code>
            ) can&apos;t be expressed by this form. Choosing an option below <strong>replaces</strong>{' '}
            it; Cancel keeps it as is.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label>Override</Label>
        {intents.length === 1 && intent !== undefined ? (
          // One possible action, preselected — a disabled dropdown reads as "not allowed"
          <p className="text-sm font-medium">{intents[0].label}</p>
        ) : (
          <Select
            value={intent ?? ''}
            onValueChange={next => {
              if (next === 'platform-off' || next === 'min-version') {
                setValue('intent', next, { shouldValidate: true });
              }
            }}
          >
            <SelectTrigger aria-label="Override type">
              <SelectValue placeholder="Choose what this override should do" />
            </SelectTrigger>
            <SelectContent>
              {intents.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {errors.intent && <p className="text-xs text-destructive">{errors.intent.message}</p>}
      </div>

      {intent === 'min-version' ? (
        <div className="space-y-2">
          <Label htmlFor={`gate-${platform}-min-version`}>Minimum app version</Label>
          <Input
            id={`gate-${platform}-min-version`}
            placeholder="48.3.11"
            {...register('minVersion')}
            aria-invalid={errors.minVersion ? true : undefined}
          />
          <p className="text-xs text-muted-foreground">
            Clients below this version get the flag off; everyone at or above it gets the
            flag&apos;s stored value.
            {platform === 'all' && ' Web is unaffected — web clients don’t report a version.'}
          </p>
          {errors.minVersion && (
            <p className="text-xs text-destructive">{errors.minVersion.message}</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {platform === 'web'
            ? 'Turns the flag off for every Web client. Web doesn’t report an app version, so version conditions can’t apply here.'
            : `Turns the flag off for every ${PLATFORM_LABELS[platform]} client, on every version.`}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
          Save
        </Button>
      </div>
    </form>
  );
}
